import React from "react"
import { Helmet } from "react-helmet"

import type { ProductSchemaProps } from "../../types/schema"

/**
 * ProductSchema component for adding Product structured data (JSON-LD)
 * This helps search engines and AI crawlers understand product information
 * and enables rich snippets in search results.
 *
 * @see https://schema.org/Product
 * @see https://developers.google.com/search/docs/appearance/structured-data/product
 */
export const ProductSchema: React.FC<ProductSchemaProps> = ({
  availability,
  brand = "Urban Armor Gear",
  condition = "NewCondition",
  description,
  image,
  name,
  price,
  priceCurrency = "USD",
  rating,
  reviewCount,
  sku,
  url,
}) => {
  // Build the schema object
  const schema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    brand: {
      "@type": "Brand",
      name: brand,
    },
    description,
    image,
    name,
    offers: {
      "@type": "Offer",
      availability: `https://schema.org/${availability}`,
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "US",
        merchantReturnDays: 30,
        returnFees: "https://schema.org/FreeReturn",
        returnMethod: "https://schema.org/ReturnByMail",
        returnPolicyCategory:
          "https://schema.org/MerchantReturnFiniteReturnWindow",
        url: "https://support.urbanarmorgear.com/hc/en-us/articles/202346517-Return-Policy",
      },
      itemCondition: `https://schema.org/${condition}`,
      price,
      priceCurrency,
      seller: {
        "@type": "Organization",
        name: "Urban Armor Gear",
      },
      url:
        url ||
        (typeof window !== "undefined" ? window.location.href : undefined),
    },
    sku,
  }

  // Add aggregate rating if available
  if (rating && reviewCount) {
    schema["aggregateRating"] = {
      "@type": "AggregateRating",
      ratingValue: rating,
      reviewCount,
    }
  }

  return (
    <Helmet as={React.Fragment as any}>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  )
}
